import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorParser } from '@app/classes';
import { AnalyticEvents } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { Salary } from '@app/models/employee/salary.model';
import { CompensationHistoryReadOnlyTable } from '@app/modules/employees/services/tables/compensation-history/compensation-history-read-only.table';
import { BaseForm } from '@app/modules/forms/base.form';
import { PrimaryCompensationForm } from '@app/modules/forms/employees/components';
import { AnalyticService, AuthService, NotifyService, PayrollService } from '@app/services';
import moment from 'moment';

const COMPENSATION_OVERLAP_ERROR_MESSAGE =
    'Data.attributes Selected date overlaps with another Regular Pay. Edit the conflicting Regular Pay first.';

@Component({
    templateUrl: './compensation-update.view.template.html',
})
export class CompensationUpdateView implements OnInit {
    @ViewChild('editPrimaryCompensationForm') editPrimaryCompensationForm: PrimaryCompensationForm;
    employee: Employee;
    table: CompensationHistoryReadOnlyTable;
    disableSaveButton = false;
    salary: Salary;
    isLoading = true;
    employeeCurrentSalaries: Salary[];
    employeeFutureSalaries: Salary[];
    displayCompensationTable = false;
    employeeInitialCurrency: string | null = null;
    salaryHasBeenAssigned = false;
    showBackDatedModal = false;
    originalEffectiveAtDate: Date | null;
    originalEndsAtDate: Date | null;
    effectiveAtHasBeenBackdated = false;
    endsAtHasBeenBackdated = false;
    selectedBackDatedPeriodMessage: string | null;
    confirmRetroactivelyActionMessage: string | null;
    retroactivelyActionMessage: string | null;
    lastPaidRecurringPayrollEndDate: Date = null;

    constructor(
        private auth: AuthService,
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private analytics: AnalyticService,
        private payrollService: PayrollService
    ) {
        this.employee = this.route.snapshot.data.models.get('employee');
    }

    async ngOnInit(): Promise<void> {
        await this.assignSalary();
        await this.assignLastRecurringPayrollEndDate();
        this.employeeInitialCurrency = this.employee.currency;
        this.isLoading = false;
    }

    async assignSalary(): Promise<void> {
        const salaryId = parseInt(this.route.snapshot.params.salary, 10);
        const [salaries] = await Salary.param('company', this.auth.company.id)
            .param('employee', this.employee.id)
            .get();
        salaries.filter((salary: Salary) => {
            if (salary.id === salaryId) {
                this.salary = salary;
                this.originalEffectiveAtDate = this.salary.effectiveAt;
                this.originalEndsAtDate = this.salary.endsAt;
                this.salaryHasBeenAssigned = true;
                return;
            }
        });
    }

    async assignLastRecurringPayrollEndDate(): Promise<void> {
        if (!this.employee.isPayrollSyncEnabled) {
            return;
        }
        try {
            this.lastPaidRecurringPayrollEndDate = await this.payrollService.getLastRecurringPayrollEndDate(
                this.auth.company.id
            );
        } catch (error) {
            this.notify.error(ErrorParser.parse(error));
        }
    }

    handleDisplayVacationPayInCompensationHistoryTable(displayVacationPayInCompensationTable: boolean): void {
        this.table = new CompensationHistoryReadOnlyTable(
            this.auth,
            this.employee,
            displayVacationPayInCompensationTable
        );
        this.displayCompensationTable = true;
    }

    submitAndValidate(form: BaseForm | undefined): boolean {
        if (form) {
            form.submit();
            return form.valid;
        }

        return true;
    }

    async handleCompensationUpdate(): Promise<void> {
        const effectiveAtHasBeenUpdated = this.salary.effectiveAt !== this.originalEffectiveAtDate;
        const endsAtHasBeenUpdated = this.salary.endsAt !== this.originalEndsAtDate;
        if (!this.employee.isPayrollSyncEnabled || (!effectiveAtHasBeenUpdated && !endsAtHasBeenUpdated)) {
            this.onSave();
        } else {
            this.effectiveAtHasBeenBackdated = effectiveAtHasBeenUpdated
                ? moment(this.lastPaidRecurringPayrollEndDate).isAfter(this.salary.effectiveAt)
                : false;
            this.endsAtHasBeenBackdated = endsAtHasBeenUpdated
                ? moment(this.lastPaidRecurringPayrollEndDate).isAfter(this.salary.endsAt)
                : false;
            if (!this.effectiveAtHasBeenBackdated && !this.endsAtHasBeenBackdated) {
                this.onSave();
            } else {
                this.determineBackDateMessaging(this.effectiveAtHasBeenBackdated, this.endsAtHasBeenBackdated);
            }
        }
    }

    determineBackDateMessaging(effectiveAtHasBeenBackdated: boolean, endsAtHasBeenBackdated: boolean): void {
        this.selectedBackDatedPeriodMessage =
            effectiveAtHasBeenBackdated && endsAtHasBeenBackdated
                ? 'employees.compensation.selectedEffectiveDateAndEndDateIncluded'
                : this.effectiveAtHasBeenBackdated && !this.endsAtHasBeenBackdated
                  ? 'employees.compensation.selectedEffectiveDateIncluded'
                  : 'employees.compensation.selectedEndDateIncluded';

        this.confirmRetroactivelyActionMessage =
            effectiveAtHasBeenBackdated && endsAtHasBeenBackdated
                ? 'employees.compensation.confirmRetroactivelyUpdate'
                : this.effectiveAtHasBeenBackdated && !this.endsAtHasBeenBackdated
                  ? 'employees.compensation.confirmRetroactivelyStart'
                  : 'employees.compensation.confirmRetroactivelyEnd';

        this.retroactivelyActionMessage =
            effectiveAtHasBeenBackdated && endsAtHasBeenBackdated
                ? 'employees.compensation.retroactivelyUpdateCompensation'
                : this.effectiveAtHasBeenBackdated && !this.endsAtHasBeenBackdated
                  ? 'employees.compensation.retroactivelyStartCompensation'
                  : 'employees.compensation.retroactivelyEndCompensation';

        this.showBackDatedModal = effectiveAtHasBeenBackdated || endsAtHasBeenBackdated;
    }

    async onSave(): Promise<void> {
        const isDirty = this.editPrimaryCompensationForm.form.dirty;

        if (!this.submitAndValidate(this.editPrimaryCompensationForm)) {
            this.showBackDatedModal = false;
            return;
        }

        try {
            this.isLoading = true;
            if (this.salary.vacationPayPercentage === undefined) {
                this.salary.vacationPayPercentage = null;
            }

            if (this.employee.currency !== this.employeeInitialCurrency) {
                await this.employee.save();
            }

            const compensationPromise = this.salary
                .param('company', this.auth.company.id)
                .param('employee', this.employee.id)
                .save();

            await compensationPromise;

            // Only track analytics if fields have changed
            if (isDirty) {
                this.analytics.trackEvent(AnalyticEvents.EmployeeProfileJobAndPayTabHistoricalCompensationEditSave);
            }

            this.notify.success('employees.compensation.compensationSaved');
            this.redirect();
        } catch (err) {
            this.isLoading = false;
            this.notify.error(err);
            if (err?.message === COMPENSATION_OVERLAP_ERROR_MESSAGE) {
                this.analytics.trackEvent(AnalyticEvents.EmployeeProfileJobAndPayTabCompensationUpdateOverlapError);
            }
        }
    }

    onCancel(): void {
        this.redirect();
    }

    redirect(): void {
        this.router.navigate(['/employees/directory', this.employee.id, 'job-and-pay'], { relativeTo: this.route });
    }

    handleSaveButtonDisable(isDisabled: boolean): void {
        this.disableSaveButton = isDisabled;
    }
}
